import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "placement", "start", "placementControls", "previous", "next"
  ]

  static values = {
    phase: String
  }

  connect() {
    this.reconcile()
  }

  placementMorphed(event) {
    const placement = event.target
    if (!this.placementTargets.includes(placement)) return

    queueMicrotask(() => {
      const placements = this.orderedPlacements

      if (this.isApplied(placement)) {
        const next = placements.find((candidate) => !this.isApplied(candidate))
        this.selectPlacement(next, placements)
        this.phaseValue = next ? "navigation-pending" : "complete"
      } else {
        this.selectPlacement(placement, placements)
        this.phaseValue = "awaiting-mark"
      }

      this.reconcile(placements)
    })
  }

  // A failed submission has no Turbo replacement, so ask the pending placement to restore its local presentation.
  rollbackFailedMarkSubmission(event) {
    if (event.detail.success) return

    const placement = this.placementTargets.find((candidate) => candidate.getAttribute("aria-busy") === "true")
    if (!placement) return

    placement.dispatchEvent(new CustomEvent("signing-placement:rollback", {bubbles: true}))
  }

  async start() {
    const placements = this.orderedPlacements
    const placement = placements.find((candidate) => !this.isApplied(candidate))
    if (!placement) return

    this.selectPlacement(placement, placements)
    this.phaseValue = "awaiting-mark"
    this.reconcile(placements)
    await this.navigateTo(placement)
  }

  async showPreviousPlacement() {
    const placements = this.orderedPlacements
    const current = placements.find((placement) => this.state(placement) === "current")
    const currentIndex = current ? placements.indexOf(current) : placements.length
    const previous = placements.slice(0, currentIndex).reverse()
      .find((placement) => this.isApplied(placement))
    if (!previous) return

    this.selectPlacement(previous, placements)
    this.phaseValue = "browsing"
    this.reconcile(placements)
    await this.navigateTo(previous)
  }

  async showNextPlacement() {
    const placements = this.orderedPlacements
    const current = placements.find((placement) => this.state(placement) === "current")
    if (!current) return

    if (this.phaseValue === "navigation-pending" && !this.isApplied(current)) {
      this.phaseValue = "awaiting-mark"
      this.reconcile(placements)
      await this.navigateTo(current)
      return
    }

    const currentIndex = placements.indexOf(current)
    const hasIncomplete = placements.some((placement) => !this.isApplied(placement))

    if (!hasIncomplete) {
      const following = placements[currentIndex + 1]
      if (!following) return

      this.selectPlacement(following, placements)
      this.phaseValue = "browsing"
      this.reconcile(placements)
      await this.navigateTo(following)
      return
    }

    const next = placements.slice(currentIndex + 1)
      .find((placement) => !this.isApplied(placement)) ||
      placements.find((placement) => !this.isApplied(placement))
    if (!next) return

    this.selectPlacement(next, placements)
    this.phaseValue = "awaiting-mark"
    this.reconcile(placements)
    await this.navigateTo(next)
  }

  get orderedPlacements() {
    return [...this.placementTargets]
  }

  state(placement) {
    return placement.dataset.signingPlacementStateValue
  }

  isApplied(placement) {
    return placement.dataset.signingPlacementAppliedValue === "true"
  }

  selectPlacement(selected, placements = this.orderedPlacements) {
    placements.forEach((placement) => {
      placement.dataset.signingPlacementStateValue = placement === selected ?
        "current" : (this.isApplied(placement) ? "completed" : "upcoming")
    })
  }

  reconcile(placements = this.orderedPlacements) {
    const current = placements.find((placement) => this.state(placement) === "current")
    const currentIndex = current ? placements.indexOf(current) : placements.length
    const hasPrevious = placements.slice(0, currentIndex)
      .some((placement) => this.isApplied(placement))
    const hasIncomplete = placements.some((placement) => !this.isApplied(placement))
    const started = this.phaseValue !== "not-started"
    const currentIsApplied = current && this.isApplied(current)
    const nextEnabled = Boolean(current) && (currentIsApplied ?
      (hasIncomplete || Boolean(placements[currentIndex + 1])) :
      this.phaseValue === "navigation-pending")

    if (this.hasStartTarget) {
      this.startTarget.disabled = !hasIncomplete
      this.startTarget.classList.toggle("hidden", started)
    }
    if (this.hasPlacementControlsTarget) this.placementControlsTarget.classList.toggle("hidden", !started)
    if (this.hasPreviousTarget) this.previousTarget.disabled = !hasPrevious
    if (this.hasNextTarget) this.nextTarget.disabled = !nextEnabled
  }

  async navigateTo(placement) {
    const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
    placement.scrollIntoView({behavior, block: "center", inline: "nearest"})
    placement.dispatchEvent(new CustomEvent("signing-placement:focus", {bubbles: true}))
  }
}
